import React, { useRef, useState } from "react";
import { generateResponse } from "../../../util/chatUtils";

// TODO (backend): The existing POST /gpt/query endpoint accepts { doc_id, query }.
// For tenant-level KB chat this needs to accept { tenant_id, query } instead.
// Until that endpoint exists, this step uses a local no-op and shows a clear TODO state.
// When the backend is ready:
//   1. Update generateResponse (or create a parallel generateTenantResponse) to send { tenant_id, query }
//   2. Remove the BACKEND_READY flag below and set it to true
const BACKEND_READY = false;

const SendIcon = () => (
  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" />
  </svg>
);

const ChatBubble = ({ chat }) => (
  <div className="space-y-2">
    <div className="flex justify-end">
      <div className="max-w-[80%] rounded-2xl rounded-tr-sm bg-[#096dea] px-4 py-2.5 text-sm text-white">
        {chat.question}
      </div>
    </div>
    <div className="flex justify-start">
      <div className="max-w-[80%] rounded-2xl rounded-tl-sm border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-800 shadow-sm">
        {chat.isLoading ? (
          <span className="inline-flex gap-1">
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.3s]" />
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.15s]" />
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" />
          </span>
        ) : chat.response ? (
          <span dangerouslySetInnerHTML={{ __html: chat.response }} />
        ) : (
          <span className="text-slate-400 italic">No response.</span>
        )}
      </div>
    </div>
  </div>
);

const OnboardingStepTest = ({ tenantId }) => {
  const [query, setQuery] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const chatEndRef = useRef(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSend = () => {
    if (!query.trim() || loading) return;
    const trimmed = query.trim();
    setQuery("");
    setErrorMessage("");

    if (!BACKEND_READY) {
      // TODO (backend): Remove this block once POST /gpt/query supports { tenant_id, query }
      setChatHistory((prev) => [
        ...prev,
        {
          question: trimmed,
          response:
            "⚠️ AI chat preview is not yet connected. The backend endpoint needs to support <code>tenant_id</code>-scoped queries. See TODO in OnboardingStepTest.js.",
          isLoading: false,
        },
      ]);
      setTimeout(scrollToBottom, 50);
      return;
    }

    // TODO (backend): Replace `id` with a tenant_id-scoped query param once the endpoint supports it.
    generateResponse({
      id: tenantId,
      query: trimmed,
      setChatHistory,
      scrollToBottom,
      setLoading,
      setErrorMessage,
      setIsMessageShow: () => {},
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClear = () => {
    setChatHistory([]);
    setErrorMessage("");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-slate-500">
          Test the AI chat experience against this tenant's uploaded knowledge base documents.
          Ask questions to verify document indexing and response quality.
        </p>
        {chatHistory.length > 0 ? (
          <button
            type="button"
            onClick={handleClear}
            className="shrink-0 text-xs font-medium text-slate-400 hover:text-slate-600"
          >
            Clear chat
          </button>
        ) : null}
      </div>

      {!BACKEND_READY ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-xs font-semibold text-amber-800">Backend integration pending</p>
          <p className="mt-1 text-xs text-amber-700">
            The existing <code className="rounded bg-amber-100 px-1 py-0.5">/gpt/query</code> endpoint
            accepts <code className="rounded bg-amber-100 px-1 py-0.5">doc_id</code> but tenant-level
            KB queries need a <code className="rounded bg-amber-100 px-1 py-0.5">tenant_id</code> parameter.
            You can still send messages below — they will show the TODO response until the backend is ready.
          </p>
        </div>
      ) : null}

      {/* Chat history */}
      <div className="min-h-[240px] max-h-[400px] overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50 p-4">
        {chatHistory.length === 0 ? (
          <div className="flex h-full min-h-[200px] flex-col items-center justify-center text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#eef5ff]">
              <svg className="h-6 w-6 text-[#096dea]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z" />
              </svg>
            </div>
            <p className="mt-3 text-sm font-medium text-slate-600">AI Chat Preview</p>
            <p className="mt-1 text-xs text-slate-400">
              Ask a question about this tenant's uploaded documents.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {chatHistory.map((chat, i) => (
              <ChatBubble key={i} chat={chat} />
            ))}
            <div ref={chatEndRef} />
          </div>
        )}
      </div>

      {errorMessage ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-700">
          {errorMessage}
        </div>
      ) : null}

      {/* Input */}
      <div className="flex items-end gap-2">
        <textarea
          rows={2}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask a question about this tenant's documents…"
          className="flex-1 resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-[#096dea] focus:ring-2 focus:ring-[#096dea]/20"
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={!query.trim() || loading}
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#096dea] text-white transition hover:bg-[#0959bb] disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Send message"
        >
          <SendIcon />
        </button>
      </div>
      <p className="text-[11px] text-slate-400">Press Enter to send · Shift+Enter for new line</p>
    </div>
  );
};

export default OnboardingStepTest;
